import * as z from 'zod'
import { Location, RentalService, Car } from '~/shared/api'
import { RentalContractStatusEnum } from '~/shared/config/enums'
import { dateSchema } from '~/shared/lib/schemas'
import { ApiModel, ToManyRelation, ToOneRelation } from './core'

const attributesSchema = z.object({
  bookedUntil: dateSchema.optional().nullable(),
  contractNumber: z.string(),
  firstDayIsFree: z.boolean(),
  rentPerDay: z.string(),
  workRuleTitle: z.string(),
  workSchedule: z.string(),
  status: z.nativeEnum(RentalContractStatusEnum),
  cancellationFeeRub: z.number().nullable(),
  cancellationFeePercentage: z.number(),
  carBrandTitle: z.string(),
  carModelTitle: z.string(),
  carPlateNumber: z.string(),
  carCategoryName: z.string(),
  isSupportOpenCloseDoors: z.boolean(),
  signedAt: dateSchema.optional().nullable(),
  promotion: z
    .object({
      discount_amount: z.number().optional(),
      discount_percent: z.number().optional(),
      duration_days: z.number().optional(),
      isActiveForDriver: z.boolean().optional(),
    })
    .optional(),
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributesSchema)

export type RentalContactAttributes = z.infer<typeof attributesSchema>

export class RentalContact extends ApiModel<
  typeof schema,
  RentalContactAttributes
> {
  static jsonApiType = 'rental-contracts'

  static schema = schema

  getCarImage(): string | undefined {
    return this.getAttribute('carImage')
  }
  getStatus(): RentalContractStatusEnum {
    return this.getAttribute('status')
  }
  getBookedUntil(): string {
    return this.getAttribute('bookedUntil')
  }
  getCreatedAt(): string {
    return this.getAttribute('createdAt')
  }
  getCancellationFeeRub(): number | null {
    return this.getAttribute('cancellationFeeRub')
  }
  getSignedAt(): string | null {
    return this.getAttribute('signedAt')
  }

  carLocation(): ToOneRelation<Location, this> {
    return this.hasOne(Location)
  }
  getCarLocation(): Location {
    return this.getRelation('carLocation')
  }

  car(): ToOneRelation<Car, this> {
    return this.hasOne(Car)
  }
  getCar(): Car {
    return this.getRelation('car')
  }

  getPromotion() {
    return this.getAttribute('promotion')
  }

  rentalServices(): ToManyRelation<RentalService, this> {
    return this.hasMany(RentalService)
  }
  getRentalServices(): RentalService[] {
    return this.getRelation('rentalServices') ?? []
  }

  static async my() {
    const response = await RentalContact.with('car')
      .with('carLocation')
      .with('rentalServices')
      .find('my')
    return response?.getData() as RentalContact
  }

  static async file(): Promise<Blob> {
    const url = `${RentalContact.getJsonApiUrl()}/file`
    const client = RentalContact.httpClient.getImplementingClient()

    const response = await client.get(url, {
      responseType: 'blob',
    })

    return response?.data
  }

  static async confirmFile(): Promise<Blob> {
    const url = `${RentalContact.getJsonApiUrl()}/confirm-file`
    const client = RentalContact.httpClient.getImplementingClient()

    const response = await client.get(url, {
      responseType: 'blob',
    })

    return response?.data
  }

  static async confirmCode(): Promise<{ message: string }> {
    const url = `${RentalContact.getJsonApiUrl()}/confirm-code`
    const client = RentalContact.httpClient.getImplementingClient()

    const response = await client.post(url)

    return response?.data
  }

  static async confirm(confirmCode: string) {
    const url = `${RentalContact.getJsonApiUrl()}/confirm`
    const client = RentalContact.httpClient.getImplementingClient()

    return await client.post(url, { confirmCode })
  }

  static async updateRentalServices(rentalServicesIds: string[]) {
    const url = `${RentalContact.getJsonApiUrl()}/update-rental-services`
    const client = RentalContact.httpClient.getImplementingClient()

    await client.patch(url, { rentalServices: rentalServicesIds })
  }

  static async signingCancel() {
    const url = `${RentalContact.getJsonApiUrl()}/signing-cancel`
    const client = RentalContact.httpClient.getImplementingClient()

    await client.patch(url)
  }
}
